import { useState, useEffect, useRef, useCallback } from 'react'
import {
  useRecoilValue,
  useRecoilState,
  useSetRecoilState,
  type RecoilState,
} from 'recoil'
import {
  selectedProductsSelector,
  universalThemeSelector,
  orderAtom,
  selectedProductsAtom,
  applicationTypeAtom,
  currentProductIdAtom,
  mappedProductsAtom,
} from 'atoms'
import { productTabAtom } from '@/atoms/_printPackageAtoms'

import { PrintPackageHeader, CustomizeProduct } from './'
import { CustomizeTabs } from 'components'
import type { CustomizeTab } from './CustomizeTabs/CustomizeTabs'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useTranslation } from 'react-i18next'
import {
  EDIT_TOKEN_STORAGE_KEYS,
  PDF_STATUS_KEY,
  STATUS_KEY,
  M3,
} from '@messenger/consts'
import type {
  ApplicationType,
  GetLayoutsFromProductsNormalized,
  PdfStatusKey,
  StatusKey,
} from '@messenger/types'
import type { ProofGalleryDetails } from '@/hooks/pdf/usePdfViewer'
import { getOrderLayouts } from '@/api/orderLayouts'
import { getLayoutById } from '@/api/layouts'
import { getOrderProducts as getOrderProductsApi } from '@/api/orderProducts'

// Order product rows flowing through the print package. MDL rows come from
// `order_products` (snake_case), M3 rows from the storefront (camelCase), so
// only the fields this component reads are pinned down here.
type PackageOrderProduct = {
  id: string
  orderProductId?: string
  name: string
  status?: StatusKey | string
  nickname?: string | null
  selected?: boolean
  basePrice?: unknown
  total?: unknown
  caseId?: string
  [key: string]: unknown
}

type MappedProduct = PackageOrderProduct & {
  layouts: GetLayoutsFromProductsNormalized[]
  background_type: string | null
}

export type ProductPdfState = {
  generating: boolean
  generatingText: string
  pendingCloseOperation: boolean
  generationState: {
    progress: number
    status: PdfStatusKey
    pdf: string
  }
  showPdfViewer: boolean
  disclaimerAcknowledged: boolean
  previewCustomizeProductHeaderDropdownsDisabled: boolean
  canCustomize: boolean
  canRevertFinalization: boolean
  isProof: boolean
  shouldSetProductStatusToFinalized: boolean
  shouldSetProductStatusToEditing: boolean
  shouldSetProductStatusToProofSent: boolean
  proofGalleryDetails: ProofGalleryDetails | null
}

type CustomizePrintProps = {
  // Passed through untouched to PrintPackageHeader (still JS/untyped).
  caseInfo?: unknown
}

// The atoms below still live in untyped JS files; cast them to the shapes this
// component relies on until the atom files are migrated to TypeScript.
const typedOrderAtom = orderAtom as RecoilState<{
  id?: string
  case_id?: string
}>
const typedApplicationTypeAtom =
  applicationTypeAtom as RecoilState<ApplicationType>
const typedCurrentProductIdAtom = currentProductIdAtom as RecoilState<
  string | null
>
const typedMappedProductsAtom = mappedProductsAtom as unknown as RecoilState<
  MappedProduct[]
>
const typedSelectedProductsAtom =
  selectedProductsAtom as unknown as RecoilState<PackageOrderProduct[]>
const typedSelectedProductsSelector = selectedProductsSelector as RecoilState<
  PackageOrderProduct[] | null
>

export const CustomizePrint = ({ caseInfo }: CustomizePrintProps) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('CustomizePrint')
  const selectedTheme = useRecoilValue(universalThemeSelector)
  const order = useRecoilValue(typedOrderAtom)
  const applicationType = useRecoilValue(typedApplicationTypeAtom)
  const isM3 = applicationType === M3
  const [currentProductId, setCurrentProductId] = useRecoilState(
    typedCurrentProductIdAtom,
  )
  const setMappedProducts = useSetRecoilState(typedMappedProductsAtom)
  const [fetchedOrderProducts, setOrderProducts] = useRecoilState(
    typedSelectedProductsAtom,
  )
  const [selectedProducts] = useRecoilState(typedSelectedProductsSelector)
  const [activeTab, setActiveTab] = useRecoilState(productTabAtom)
  const [mappedProductsArr, setMappedProductsArr] = useState<MappedProduct[]>(
    [],
  )
  const [productsTabs, setProductTabs] = useState<CustomizeTab[]>([])
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const prevDeps = useRef({
    fetchedOrderProducts,
    selectedTheme,
    selectedProducts,
  })

  // PDF state management - keyed by product ID for persistence across tab switches
  const [pdfStates, setPdfStates] = useState(new Map<string, ProductPdfState>())

  const getPdfState = useCallback(
    (
      productId: string,
      productStatus?: StatusKey | string,
    ): ProductPdfState => {
      const existingState = pdfStates.get(productId)
      if (existingState) {
        return existingState
      }

      const initialCanRevertFinalization =
        productStatus === STATUS_KEY.FINALIZED
      const initialCanCustomize = productStatus !== STATUS_KEY.FINALIZED

      return {
        generating: false,
        generatingText: isM3
          ? t('pdf.generateProofM3')
          : t('pdf.generateProof'),
        pendingCloseOperation: false,
        generationState: {
          progress: 0,
          status: PDF_STATUS_KEY.IN_QUEUE,
          pdf: '',
        },
        showPdfViewer: false,
        disclaimerAcknowledged: false,
        previewCustomizeProductHeaderDropdownsDisabled: false,
        canCustomize: initialCanCustomize,
        canRevertFinalization: initialCanRevertFinalization,
        isProof: false,
        shouldSetProductStatusToFinalized: false,
        shouldSetProductStatusToEditing: false,
        shouldSetProductStatusToProofSent: false,
        proofGalleryDetails: null,
      }
    },
    [pdfStates, t, isM3],
  )

  const updatePdfState = useCallback(
    (productId: string, updates: Partial<ProductPdfState>) => {
      setPdfStates((currentStates) => {
        const newStates = new Map(currentStates)
        const currentState =
          newStates.get(productId) || getPdfState(productId, STATUS_KEY.EDITING)
        const updatedState = { ...currentState, ...updates }
        newStates.set(productId, updatedState)
        return newStates
      })
    },
    [getPdfState],
  )

  const getOrderProducts = async () => {
    try {
      const { data: orderProductsData } = await getOrderProductsApi(
        order.id,
        order.case_id,
        applicationType,
      )
      setOrderProducts(orderProductsData as unknown as PackageOrderProduct[])
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string } }
        message?: string
      }
      handleError(
        err?.response?.data?.message || err?.message || String(error),
        error as Error,
      )
    }
  }

  const mapLayouts = async (products: PackageOrderProduct[]) => {
    try {
      const mappedProducts: MappedProduct[] = []
      for (const product of products) {
        if (product.id) {
          const isM3 =
            'basePrice' in product || 'total' in product || 'caseId' in product

          const { data: orderLayouts } = await getOrderLayouts(product.id)
          const layoutId =
            Array.isArray(orderLayouts) && orderLayouts.length > 0
              ? orderLayouts[0].layout_id
              : undefined
          if (layoutId) {
            const { data: layout } = await getLayoutById(layoutId, isM3)
            const { background_type } = layout
            const mappedProductObject = {
              ...product,
              layouts: Array.isArray(orderLayouts)
                ? orderLayouts
                : [orderLayouts],
              background_type,
            }
            mappedProducts.push(mappedProductObject)
          } else {
            console.warn(
              `No layouts found for product.id: ${product.id}. Using product data without layout enhancements.`,
            )
            mappedProducts.push({
              ...product,
              layouts: [],
              background_type: null,
            })
          }
        }
      }
      setMappedProductsArr(mappedProducts)
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string } }
        message?: string
      }
      handleError(
        err?.response?.data?.message || err?.message || String(error),
        error as Error,
      )
    }
  }

  useEffect(() => {
    if (order.id) {
      getOrderProducts()
    }
    //eslint-disable-next-line
  }, [order])

  useEffect(() => {
    const currentDeps = {
      fetchedOrderProducts,
      selectedTheme,
      selectedProducts,
    }

    prevDeps.current = currentDeps

    if (fetchedOrderProducts && fetchedOrderProducts.length > 0) {
      mapLayouts(fetchedOrderProducts)
    } else if (fetchedOrderProducts) {
      setMappedProductsArr([])
    }
    //eslint-disable-next-line
  }, [fetchedOrderProducts, selectedTheme, selectedProducts])

  const getProductTitle = (product: MappedProduct) => {
    let title = product.name.includes('Register Book:')
      ? product.name.split(': ')[1]
      : product.name

    const isFinalized = product.status === 'FINALIZED'
    if (isFinalized) title = `${title} ✓`

    return title
  }

  const getTabs = (): CustomizeTab[] => {
    // A token-bound product scope (stored at token validation from the
    // server, never taken from URL parameters) limits the editor to that
    // one product — the Designer Space opens requests this way, so a
    // designer only ever sees the line they were asked to design. The
    // customer's personalize tokens carry no scope and keep every
    // product on the case.
    const scopedProductId = sessionStorage.getItem(
      EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId,
    )

    const sortedProductsAndLayouts = mappedProductsArr
      .filter((product) => product.selected)
      .filter(
        (product) =>
          !scopedProductId ||
          (product.id || product.orderProductId) === scopedProductId,
      )
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1
      })

    // Set initial active tab ONCE before mapping
    if (sortedProductsAndLayouts.length > 0) {
      let targetProductId: string | null = null

      // Priority 1: Use currentProductId if it exists in the products list (for M3)
      if (currentProductId) {
        const storedProductExists = sortedProductsAndLayouts.find(
          (p) => (p.id || p.orderProductId) === currentProductId,
        )
        if (storedProductExists) {
          targetProductId = currentProductId
        }
      }

      // Priority 2: If activeTab is set, try to preserve it (for MDL and tab persistence)
      if (!targetProductId && activeTab) {
        const activeTabExists = sortedProductsAndLayouts.find(
          (p) => (p.id || p.orderProductId) === activeTab,
        )
        if (activeTabExists) {
          targetProductId = activeTab
        }
      }

      // Priority 3: If no valid stored product, use the first product
      if (!targetProductId) {
        targetProductId =
          sortedProductsAndLayouts[0]?.id ||
          sortedProductsAndLayouts[0]?.orderProductId ||
          null
      }

      // Only update if needed
      if (targetProductId && activeTab !== targetProductId) {
        setActiveTab(targetProductId)

        // Ensure currentProductId is in sync for per-product theme resolution
        if (currentProductId !== targetProductId) {
          setCurrentProductId(targetProductId)
        }
      }
    }

    return sortedProductsAndLayouts.map((product) => {
      const productId = product.id || product.orderProductId
      const title = getProductTitle(product)
      const currentPdfState = getPdfState(product.id, product.status)

      return {
        id: productId,
        title,
        nickname: product.nickname || null,
        content: (
          <CustomizeProduct
            productInfo={product}
            themeInfo={selectedTheme}
            productsAndLayouts={sortedProductsAndLayouts}
            getOrderProducts={getOrderProducts}
            pdfState={currentPdfState}
            updatePdfState={(updates: Partial<ProductPdfState>) =>
              updatePdfState(product.id, updates)
            }
          />
        ),
      }
    })
  }

  const setActiveTabAndPersist = (tabId: string) => {
    // If clicking the same tab, don't do anything
    if (tabId === activeTab) return

    // Clear any existing timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    // Show transition loading state
    setIsTransitioning(true)

    // Set the new active tab
    setActiveTab(tabId)

    // Update current product ID so the theme selector resolves the correct per-product theme
    const clickedProduct = mappedProductsArr.find((product) => {
      const productId = product.id || product.orderProductId
      return productId === tabId
    })

    if (clickedProduct && tabId) {
      setCurrentProductId(tabId)
    }

    // Wait 500ms before hiding the transition loading state
    // This gives the new component time to mount and start its own loading
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false)
      transitionTimeoutRef.current = null
    }, 500)
  }

  useEffect(() => {
    const tabs = getTabs()
    setProductTabs(tabs)

    // Update the mapped products atom for the selectors
    setMappedProducts(mappedProductsArr)
    //eslint-disable-next-line
  }, [mappedProductsArr, selectedProducts, pdfStates])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <PrintPackageHeader
        title={isM3 ? t('package.titleM3') : t('package.title')}
        caseInfo={caseInfo}
        margin="0 0 32px"
      />
      <CustomizeTabs
        tabs={productsTabs}
        width="250px"
        selected={[activeTab, setActiveTabAndPersist]}
        isTransitioning={isTransitioning}
      />
    </>
  )
}

export default CustomizePrint
